import React, { useState, useMemo, useEffect } from 'react';
import { Table, Button, Form, Modal, InputGroup } from 'react-bootstrap';
import { getContrastColor, COLOR_SECONDARY, COLOR_PRIMARY, COLOR_SUCCESS } from '../../shared/colorUtils';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../shared/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';
import { CenteredLoader } from '../../components/LoadingSpinner';

const GroupManagement = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('groups');

  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [groupModalError, setGroupModalError] = useState(null);

  const [showPermModal, setShowPermModal] = useState(false);
  const [permGroup, setPermGroup] = useState(null);
  const [selectedPerms, setSelectedPerms] = useState(new Set());
  const [permSearch, setPermSearch] = useState('');

  const [showUsersModal, setShowUsersModal] = useState(false);
  const [usersGroup, setUsersGroup] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [userSearch, setUserSearch] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);

  const [showUserPermModal, setShowUserPermModal] = useState(false);
  const [userPermTarget, setUserPermTarget] = useState(null);
  const [userPermData, setUserPermData] = useState(null);
  const [userPermSearch, setUserPermSearch] = useState('');
  const [userPermTab, setUserPermTab] = useState('groups');
  const [loadingUserPerms, setLoadingUserPerms] = useState(false);

  const [usersTabSearch, setUsersTabSearch] = useState('');

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleTarget, setRoleTarget] = useState(null);
  const [roleIsStaff, setRoleIsStaff] = useState(false);
  const [roleIsAdmin, setRoleIsAdmin] = useState(false);

  const { isPending: loading, data: queryData } = useQuery({
    queryKey: ['group-management'],
    queryFn: async () => {
      const [groupData, userData, permData] = await Promise.all([
        api.get('accounts/api/groups/'),
        api.get('accounts/api/users/'),
        api.get('accounts/api/groups/available-permissions/'),
      ]);
      return {
        groups: groupData,
        users: Array.isArray(userData) ? userData : (userData.results || []),
        permissions: permData,
      };
    },
  });

  useEffect(() => {
    if (!queryData) return;
    setGroups(queryData.groups);
    setUsers(queryData.users);
    setAvailablePermissions(queryData.permissions);
  }, [queryData]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['group-management'] });

  const saveGroupMutation = useMutation({
    mutationFn: (payload) => editingGroup
      ? api.patch(`accounts/api/groups/${editingGroup.id}/`, payload)
      : api.post('accounts/api/groups/', payload),
    onSuccess: () => { setSuccess(editingGroup ? t('settings.groups.success.updated') : t('settings.groups.success.created')); setShowGroupModal(false); invalidate(); },
    onError: (err) => setGroupModalError(err.message),
  });
  const saving = saveGroupMutation.isPending;

  const deleteGroupMutation = useMutation({
    mutationFn: (id) => api.delete(`accounts/api/groups/${id}/`),
    onSuccess: () => { setSuccess(t('settings.groups.success.deleted')); setShowDeleteModal(false); invalidate(); },
    onError: (err) => { setError(err.message); setShowDeleteModal(false); },
  });

  const savePermsMutation = useMutation({
    mutationFn: ({ groupId, perms }) => api.patch(`accounts/api/groups/${groupId}/`, { permissions: perms }),
    onSuccess: () => { setSuccess(t('settings.groups.success.permissionsSaved')); setShowPermModal(false); invalidate(); },
    onError: (err) => setError(err.message),
  });
  const savingPerms = savePermsMutation.isPending;

  const saveUsersMutation = useMutation({
    mutationFn: ({ groupId, userIds }) => api.patch(`accounts/api/groups/${groupId}/`, { users: userIds }),
    onSuccess: () => { setSuccess(t('settings.groups.success.usersSaved')); setShowUsersModal(false); invalidate(); },
    onError: (err) => setError(err.message),
  });
  const savingUsers = saveUsersMutation.isPending;

  const saveUserPermsMutation = useMutation({
    mutationFn: ({ userId, permissionIds, groupIds }) =>
      api.patch(`accounts/api/users/${userId}/permissions/`, { permission_ids: permissionIds, group_ids: groupIds }),
    onSuccess: () => { setSuccess(t('settings.groups.success.userPermissionsSaved')); setShowUserPermModal(false); invalidate(); },
    onError: (err) => setError(err.message),
  });
  const savingUserPerms = saveUserPermsMutation.isPending;

  const saveRoleMutation = useMutation({
    mutationFn: ({ userId, isStaff, isAdmin }) =>
      api.patch(`accounts/api/users/${userId}/set-role/`, { is_staff: isStaff, is_superuser: isAdmin }),
    onSuccess: () => { setSuccess(t('settings.groups.success.roleSaved')); setShowRoleModal(false); invalidate(); },
    onError: (err) => setError(err.message),
  });
  const savingRole = saveRoleMutation.isPending;

  // ── Group create/edit ─────────────────────────────────────────────────────

  const openCreateModal = () => {
    setEditingGroup(null);
    setGroupName('');
    setGroupModalError(null);
    setShowGroupModal(true);
  };

  const openEditModal = (group) => {
    setEditingGroup(group);
    setGroupName(group.name);
    setGroupModalError(null);
    setShowGroupModal(true);
  };

  const handleSaveGroup = () => {
    if (!groupName.trim()) {
      setGroupModalError(t('settings.groups.groupName') + ' is required.');
      return;
    }
    setGroupModalError(null);
    saveGroupMutation.mutate({ name: groupName.trim() });
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const openDeleteModal = (group) => {
    setDeletingGroup(group);
    setShowDeleteModal(true);
  };

  const handleDelete = () => deleteGroupMutation.mutate(deletingGroup.id);

  // ── Permissions modal ─────────────────────────────────────────────────────

  const openPermModal = (group) => {
    setPermGroup(group);
    setSelectedPerms(new Set(group.permissions.map((p) => p.id)));
    setPermSearch('');
    setShowPermModal(true);
  };

  const filteredPerms = useMemo(() => {
    const q = permSearch.toLowerCase();
    return availablePermissions.filter(
      (p) =>
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.codename.toLowerCase().includes(q) ||
        p.content_type_label.toLowerCase().includes(q)
    );
  }, [availablePermissions, permSearch]);

  // Group permissions by app label for display
  const groupedPerms = useMemo(() => {
    const map = {};
    filteredPerms.forEach((p) => {
      const app = p.content_type_label.split('.')[0];
      if (!map[app]) map[app] = [];
      map[app].push(p);
    });
    return map;
  }, [filteredPerms]);

  const togglePerm = (id) => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllPerms = () => setSelectedPerms(new Set(filteredPerms.map((p) => p.id)));
  const deselectAllPerms = () => setSelectedPerms(new Set());

  const handleSavePerms = () => savePermsMutation.mutate({ groupId: permGroup.id, perms: Array.from(selectedPerms) });

  // ── Users modal ───────────────────────────────────────────────────────────

  const openUsersModal = (group) => {
    setUsersGroup(group);
    setSelectedUsers(new Set(group.user_ids || []));
    setUserSearch('');
    setShowUsersModal(true);
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.toLowerCase();
    return users.filter(
      (u) =>
        !q ||
        u.username.toLowerCase().includes(q) ||
        (u.full_name || '').toLowerCase().includes(q)
    );
  }, [users, userSearch]);

  const toggleUser = (id) => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSaveUsers = () => saveUsersMutation.mutate({ groupId: usersGroup.id, userIds: Array.from(selectedUsers) });

  // ── User permissions modal ────────────────────────────────────────────────

  const openUserPermModal = async (user) => {
    setUserPermTarget(user);
    setUserPermData(null);
    setUserPermSearch('');
    setUserPermTab('groups');
    setShowUserPermModal(true);
    setLoadingUserPerms(true);
    try {
      const data = await api.get(`accounts/api/users/${user.id}/permissions/`);
      setUserPermData({
        permission_ids: new Set(data.permission_ids),
        group_ids: new Set(data.group_ids),
      });
    } catch (err) {
      setError(err.message);
      setShowUserPermModal(false);
    } finally {
      setLoadingUserPerms(false);
    }
  };

  const toggleUserGroup = (id) =>
    setUserPermData((prev) => {
      const next = new Set(prev.group_ids);
      next.has(id) ? next.delete(id) : next.add(id);
      return { ...prev, group_ids: next };
    });

  const toggleUserDirectPerm = (id) =>
    setUserPermData((prev) => {
      const next = new Set(prev.permission_ids);
      next.has(id) ? next.delete(id) : next.add(id);
      return { ...prev, permission_ids: next };
    });

  const filteredUserPerms = useMemo(() => {
    const q = userPermSearch.toLowerCase();
    return availablePermissions.filter(
      (p) =>
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.codename.toLowerCase().includes(q) ||
        p.content_type_label.toLowerCase().includes(q)
    );
  }, [availablePermissions, userPermSearch]);

  const groupedUserPerms = useMemo(() => {
    const map = {};
    filteredUserPerms.forEach((p) => {
      const app = p.content_type_label.split('.')[0];
      if (!map[app]) map[app] = [];
      map[app].push(p);
    });
    return map;
  }, [filteredUserPerms]);

  const handleSaveUserPerms = () => saveUserPermsMutation.mutate({
    userId: userPermTarget.id,
    permissionIds: Array.from(userPermData.permission_ids),
    groupIds: Array.from(userPermData.group_ids),
  });

  // ── Users tab search ──────────────────────────────────────────────────────

  const filteredUsersTab = useMemo(() => {
    const q = usersTabSearch.toLowerCase();
    return users.filter(
      (u) =>
        !q ||
        u.username.toLowerCase().includes(q) ||
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
    );
  }, [users, usersTabSearch]);

  const getUserGroups = (user) =>
    groups.filter((g) => (g.user_ids || []).includes(user.id));

  // ── Set Role ──────────────────────────────────────────────────────────────

  const openRoleModal = (user) => {
    setRoleTarget(user);
    setRoleIsStaff(user.is_staff || false);
    setRoleIsAdmin(user.is_superuser || false);
    setShowRoleModal(true);
  };

  const handleSaveRole = () => saveRoleMutation.mutate({ userId: roleTarget.id, isStaff: roleIsStaff, isAdmin: roleIsAdmin });

  const getRoleBadge = (user) => {
    if (user.is_superuser) return { label: t('settings.groups.roleAdmin'), color: '#d63939' };
    if (user.is_staff)     return { label: t('settings.groups.roleStaff'),  color: '#f76707' };
    return                        { label: t('settings.groups.roleRegular'), color: COLOR_SECONDARY };
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <PageShell
      pretitle={t('menu.settings')}
      title={t('settings.groups.title')}
      description={t('settings.groups.subtitle')}
      actions={activeTab === 'groups' ? (
        <Button variant="primary" onClick={openCreateModal}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
            <path d="M12 5v14M5 12h14" />
          </svg>
          {t('settings.groups.createGroup')}
        </Button>
      ) : null}
    >
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}

          {/* ── Tab Nav ──────────────────────────────────────────────────── */}
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link${activeTab === 'groups' ? ' active' : ''}`}
                onClick={() => setActiveTab('groups')}
              >
                {t('settings.groups.groupsTab')}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link${activeTab === 'users' ? ' active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                {t('settings.groups.usersTab')}
              </button>
            </li>
          </ul>

          {/* ── Groups Tab ───────────────────────────────────────────────── */}
          {activeTab === 'groups' && (
            <PageSection bodyClassName="p-0">
              {loading ? (
                <CenteredLoader />
              ) : groups.length === 0 ? (
                <div className="text-center text-muted py-5">{t('settings.groups.noGroups')}</div>
              ) : (
                  <Table responsive hover className="table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>{t('settings.groups.groupName')}</th>
                        <th>{t('settings.groups.users')}</th>
                        <th>{t('settings.groups.permissions')}</th>
                        <th className="w-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map((group) => (
                        <tr key={group.id}>
                          <td className="fw-semibold">{group.name}</td>
                          <td>
                            <span
                              onClick={() => openUsersModal(group)}
                              style={{
                                cursor: 'pointer',
                                display: 'inline-block',
                                padding: '0.2em 0.55em',
                                borderRadius: '4px',
                                fontSize: '0.75em',
                                fontWeight: 600,
                                background: COLOR_SECONDARY,
                                color: getContrastColor(COLOR_SECONDARY),
                              }}
                            >
                              {t('settings.groups.userCount', { count: group.user_count })}
                            </span>
                          </td>
                          <td>
                            <span
                              onClick={() => openPermModal(group)}
                              style={{
                                cursor: 'pointer',
                                display: 'inline-block',
                                padding: '0.2em 0.55em',
                                borderRadius: '4px',
                                fontSize: '0.75em',
                                fontWeight: 600,
                                background: COLOR_PRIMARY,
                                color: getContrastColor(COLOR_PRIMARY),
                              }}
                            >
                              {t('settings.groups.permissionCount', { count: group.permissions.length })}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-2 justify-content-end">
                              <Button size="sm" variant="outline-primary" onClick={() => openPermModal(group)}>
                                {t('settings.groups.managePermissions')}
                              </Button>
                              <Button size="sm" variant="outline-secondary" onClick={() => openUsersModal(group)}>
                                {t('settings.groups.manageUsers')}
                              </Button>
                              <Button size="sm" variant="outline-warning" onClick={() => openEditModal(group)}>
                                {t('settings.groups.editGroup')}
                              </Button>
                              <Button size="sm" variant="outline-danger" onClick={() => openDeleteModal(group)}>
                                {t('settings.groups.deleteGroup')}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
              )}
            </PageSection>
          )}

          {/* ── Users Tab ────────────────────────────────────────────────── */}
          {activeTab === 'users' && (
            <PageSection
              bodyClassName="p-0"
              actions={
                <InputGroup style={{ maxWidth: 280 }}>
                  <Form.Control
                    type="text"
                    placeholder={t('settings.groups.searchUsers')}
                    value={usersTabSearch}
                    onChange={(e) => setUsersTabSearch(e.target.value)}
                  />
                </InputGroup>
              }
            >
              {loading ? (
                <CenteredLoader />
              ) : (
                  <Table responsive hover className="table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>{t('settings.groups.users')}</th>
                        <th>{t('settings.groups.role')}</th>
                        <th>{t('settings.groups.groupsTab')}</th>
                        <th className="w-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsersTab.map((user) => {
                        const userGroups = getUserGroups(user);
                        return (
                          <tr key={user.id}>
                            <td>
                              <div className="fw-semibold">{user.full_name || user.username}</div>
                              {user.full_name && (
                                <div className="text-muted small">{user.username}</div>
                              )}
                            </td>
                            <td>
                              {(() => {
                                const badge = getRoleBadge(user);
                                return (
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '0.2em 0.55em',
                                    borderRadius: '4px',
                                    fontSize: '0.75em',
                                    fontWeight: 600,
                                    background: badge.color,
                                    color: getContrastColor(badge.color),
                                  }}>
                                    {badge.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {userGroups.length === 0 ? (
                                  <span className="text-muted small">—</span>
                                ) : (
                                  userGroups.map((g) => (
                                    <span
                                      key={g.id}
                                      style={{
                                        display: 'inline-block',
                                        padding: '0.2em 0.55em',
                                        borderRadius: '4px',
                                        fontSize: '0.75em',
                                        fontWeight: 600,
                                        background: COLOR_PRIMARY,
                                        color: getContrastColor(COLOR_PRIMARY),
                                      }}
                                    >
                                      {g.name}
                                    </span>
                                  ))
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button size="sm" variant="outline-secondary" onClick={() => openRoleModal(user)}
                                  disabled={user.id === currentUser?.id}>
                                  {t('settings.groups.setRole')}
                                </Button>
                                <Button size="sm" variant="outline-primary" onClick={() => openUserPermModal(user)}>
                                  {t('settings.groups.editUserPermissions')}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredUsersTab.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      {t('settings.groups.noUsers')}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </PageSection>
      )}

      {/* ── Create / Edit Group Modal ──────────────────────────────────── */}
      <Modal show={showGroupModal} onHide={() => setShowGroupModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingGroup ? t('settings.groups.editGroup') : t('settings.groups.createGroup')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {groupModalError && <div className="alert alert-danger">{groupModalError}</div>}
          <Form.Group>
            <Form.Label>{t('settings.groups.groupName')}</Form.Label>
            <Form.Control
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder={t('settings.groups.namePlaceholder')}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveGroup()}
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGroupModal(false)} disabled={saving}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="primary" onClick={handleSaveGroup} disabled={saving}>
            {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
            {t('common.save') || 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Confirmation Modal ──────────────────────────────────────── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('settings.groups.deleteGroup')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('settings.groups.deleteConfirm')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            {t('common.delete') || 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Manage Permissions Modal ───────────────────────────────────────── */}
      <Modal show={showPermModal} onHide={() => setShowPermModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('settings.groups.managePermissions')} — {permGroup?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder={t('settings.groups.searchPermissions')}
              value={permSearch}
              onChange={(e) => setPermSearch(e.target.value)}
            />
          </InputGroup>
          <div className="mb-2 d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={selectAllPerms}>
              {t('settings.groups.selectAll')}
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={deselectAllPerms}>
              {t('settings.groups.deselectAll')}
            </Button>
          </div>
          {Object.keys(groupedPerms).sort().map((app) => (
            <div key={app} className="mb-3">
              <div className="text-uppercase text-muted small fw-semibold mb-1">{app}</div>
              {groupedPerms[app].map((perm) => (
                <Form.Check
                  key={perm.id}
                  type="checkbox"
                  id={`perm-${perm.id}`}
                  label={`${perm.name} (${perm.codename})`}
                  checked={selectedPerms.has(perm.id)}
                  onChange={() => togglePerm(perm.id)}
                  className="mb-1"
                />
              ))}
            </div>
          ))}
          {Object.keys(groupedPerms).length === 0 && (
            <div className="text-center text-muted py-3">{t('settings.groups.noPermissions')}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPermModal(false)} disabled={savingPerms}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="primary" onClick={handleSavePerms} disabled={savingPerms}>
            {savingPerms ? <span className="spinner-border spinner-border-sm me-1" /> : null}
            {t('settings.groups.savePermissions')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Manage Users Modal ─────────────────────────────────────────────── */}
      <Modal show={showUsersModal} onHide={() => setShowUsersModal(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('settings.groups.manageUsers')} — {usersGroup?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder={t('settings.groups.searchUsers')}
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          </InputGroup>
          {filteredUsers.map((user) => (
            <Form.Check
              key={user.id}
              type="checkbox"
              id={`user-${user.id}`}
              label={user.full_name ? `${user.full_name} (${user.username})` : user.username}
              checked={selectedUsers.has(user.id)}
              onChange={() => toggleUser(user.id)}
              className="mb-1"
            />
          ))}
          {filteredUsers.length === 0 && (
            <div className="text-center text-muted py-3">{t('settings.groups.noUsers')}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUsersModal(false)} disabled={savingUsers}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="primary" onClick={handleSaveUsers} disabled={savingUsers}>
            {savingUsers ? <span className="spinner-border spinner-border-sm me-1" /> : null}
            {t('settings.groups.saveUsers')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── User Permissions Modal ─────────────────────────────────────────── */}
      <Modal show={showUserPermModal} onHide={() => setShowUserPermModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('settings.groups.userPermissionsTitle', {
              name: userPermTarget?.full_name || userPermTarget?.username || '',
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingUserPerms ? (
            <CenteredLoader />
          ) : userPermData ? (
            <>
              {/* Inner tab nav */}
              <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                  <button
                    className={`nav-link${userPermTab === 'groups' ? ' active' : ''}`}
                    onClick={() => setUserPermTab('groups')}
                  >
                    {t('settings.groups.memberOf')}
                    {' '}
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '0.1em 0.45em',
                        borderRadius: '4px',
                        fontSize: '0.75em',
                        fontWeight: 600,
                        background: userPermData.group_ids.size > 0 ? COLOR_PRIMARY : COLOR_SECONDARY,
                        color: getContrastColor(
                          userPermData.group_ids.size > 0 ? COLOR_PRIMARY : COLOR_SECONDARY
                        ),
                      }}
                    >
                      {userPermData.group_ids.size}
                    </span>
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link${userPermTab === 'direct' ? ' active' : ''}`}
                    onClick={() => setUserPermTab('direct')}
                  >
                    {t('settings.groups.directPermissions')}
                    {' '}
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '0.1em 0.45em',
                        borderRadius: '4px',
                        fontSize: '0.75em',
                        fontWeight: 600,
                        background: userPermData.permission_ids.size > 0 ? COLOR_SUCCESS : COLOR_SECONDARY,
                        color: getContrastColor(
                          userPermData.permission_ids.size > 0 ? COLOR_SUCCESS : COLOR_SECONDARY
                        ),
                      }}
                    >
                      {userPermData.permission_ids.size}
                    </span>
                  </button>
                </li>
              </ul>

              {/* Member of Groups tab */}
              {userPermTab === 'groups' && (
                <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                  {groups.length === 0 ? (
                    <div className="text-center text-muted py-3">{t('settings.groups.noGroups')}</div>
                  ) : (
                    groups.map((g) => (
                      <Form.Check
                        key={g.id}
                        type="checkbox"
                        id={`uperm-group-${g.id}`}
                        label={g.name}
                        checked={userPermData.group_ids.has(g.id)}
                        onChange={() => toggleUserGroup(g.id)}
                        className="mb-1"
                      />
                    ))
                  )}
                </div>
              )}

              {/* Direct Permissions tab */}
              {userPermTab === 'direct' && (
                <div>
                  <InputGroup className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder={t('settings.groups.searchPermissions')}
                      value={userPermSearch}
                      onChange={(e) => setUserPermSearch(e.target.value)}
                    />
                  </InputGroup>
                  <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                    {Object.keys(groupedUserPerms).length === 0 ? (
                      <div className="text-center text-muted py-3">
                        {t('settings.groups.noDirectPermissions')}
                      </div>
                    ) : (
                      Object.keys(groupedUserPerms).sort().map((app) => (
                        <div key={app} className="mb-3">
                          <div className="text-uppercase text-muted small fw-semibold mb-1">{app}</div>
                          {groupedUserPerms[app].map((perm) => (
                            <Form.Check
                              key={perm.id}
                              type="checkbox"
                              id={`uperm-direct-${perm.id}`}
                              label={`${perm.name} (${perm.codename})`}
                              checked={userPermData.permission_ids.has(perm.id)}
                              onChange={() => toggleUserDirectPerm(perm.id)}
                              className="mb-1"
                            />
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUserPermModal(false)} disabled={savingUserPerms}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button variant="primary" onClick={handleSaveUserPerms} disabled={savingUserPerms || !userPermData}>
            {savingUserPerms ? <span className="spinner-border spinner-border-sm me-1" /> : null}
            {t('settings.groups.saveUserPermissions')}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* ── Set Role Modal ──────────────────────────────────────────────── */}
      <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('settings.groups.setRoleTitle', {
              name: roleTarget?.full_name || roleTarget?.username || '',
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Check
            type="checkbox"
            id="role-is-staff"
            label={t('settings.groups.isStaff')}
            checked={roleIsStaff}
            onChange={(e) => {
              setRoleIsStaff(e.target.checked);
              if (!e.target.checked) setRoleIsAdmin(false);
            }}
            className="mb-2"
          />
          <Form.Check
            type="checkbox"
            id="role-is-admin"
            label={t('settings.groups.isSuperuser')}
            checked={roleIsAdmin}
            onChange={(e) => {
              setRoleIsAdmin(e.target.checked);
              if (e.target.checked) setRoleIsStaff(true);
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRoleModal(false)} disabled={savingRole}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSaveRole} disabled={savingRole}>
            {savingRole ? <span className="spinner-border spinner-border-sm me-1" /> : null}
            {t('settings.groups.saveRole')}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
};

export default GroupManagement;
